import { useContext, useEffect, useState } from "react";
import {
  AlertComponent,
  DialogComponent,
  ModalComponent,
  UploadFileForm,
} from "../../../components";
import { useAppDesignPdf, useRequest } from "../../../hooks";
import {
  ClientProps,
  DocumentProps,
  WellProps,
} from "../../../interfaces/interfaces";
import { DocRow, DocumentsPortalLayout } from "../../../components/documents/DocumentsPortalLayout";
import DataContext from "../../../context/DataContext";
import { useNavigate } from "react-router-dom";

const isSuccessful = (r: any) => r?.success || r?.succes;
const getRows = (r: any) =>
  Array.isArray(r?.data) ? r.data : r?.data?.rows ?? [];

const latestUploadByPullingId = (rows: DocRow[]) => {
  const map = new Map<number, DocRow>();
  rows.forEach((row) => {
    const pid = row.pullingDesignId;
    if (!pid) return;
    const cur = map.get(pid);
    if (!cur || new Date(row.date).getTime() > new Date(cur.date).getTime())
      map.set(pid, row);
  });
  return map;
};

export const PullingReportAdmin = () => {
  const navigate = useNavigate();
  const { handleRequest } = useRequest();
  const { downloadPullingPdf } = useAppDesignPdf();
  const { clientOptions, fetchDataClient, onDeleteDocument } = useContext(DataContext);

  const [wells, setWells] = useState<WellProps[]>([]);
  const [rows, setRows] = useState<DocRow[]>([]);
  const [wellsLoading, setWellsLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientProps | null>(null);

  useEffect(() => { fetchDataClient(); }, []);

  useEffect(() => {
    setTimeout(() => { setIsError(false); setIsSuccess(false); }, 4000);
  }, [isError, isSuccess]);

  const handleClientChange = (client: ClientProps | null) => {
    setSelectedClient(client);
    setSelectedWellId(null);
    if (!client) { setWells([]); setRows([]); return; }
    const opts: RequestInit = { method: "GET" };
    setWellsLoading(true);
    setRowsLoading(true);
    Promise.all([
      handleRequest({ endpoint: `well/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `pulling-list/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `pulling-detail?client=${client.id}`, options: opts }),
    ])
      .then(([wellsRes, uploadedRes, appRes]) => {
        const wellData: WellProps[] = isSuccessful(wellsRes) ? wellsRes.data : [];
        setWells(wellData);
        setWellsLoading(false);
        const wellMap = new Map<number, string>(wellData.map((w) => [w.id as number, w.name]));
        const uploaded: DocRow[] = (isSuccessful(uploadedRes) ? uploadedRes.data : []).map(
          (item: any) => ({
            rowId: `u-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            installationDate: item.installationDate,
            wellId: item.idWell,
            wellName: wellMap.get(item.idWell) ?? "",
            source: "uploaded" as const,
            path: item.path,
            owner: "—",
            pullingDesignId: item.pullingDesignId,
          }),
        );
        const uploadsByPulling = latestUploadByPullingId(uploaded);
        const app: DocRow[] = getRows(appRes).map((item: any) => {
          const def = uploadsByPulling.get(item.id);
          return {
            rowId: `a-${item.id}`,
            customName: item.customName || item.pullingName,
            date: item.createdAt || item.pullingDate || "",
            installationDate: item.installationDate,
            wellId: item.well?.id,
            wellName: item.well?.name ?? "",
            source: "app" as const,
            appId: item.id,
            owner: "—",
            defaultUploadPath: def?.path,
            defaultUploadName: def?.customName,
          };
        });
        const sorted = [...uploaded, ...app].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setRows(sorted);
        setRowsLoading(false);
      })
      .catch(() => { setIsError(true); setWellsLoading(false); setRowsLoading(false); });
  };

  const fetchPullingByKey = (key: string, name: string, download: boolean) => {
    setIsCreatingReport(true);
    const opts: RequestInit = {
      method: "POST",
      body: JSON.stringify({ key }),
      headers: { responseType: "Buffer" } as any,
    };
    handleRequest({ endpoint: "pdf-pulling-adm", options: opts })
      .then((res) => {
        if (res.success) {
          const url = window.URL.createObjectURL(
            new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
          );
          if (download) {
            const link = document.createElement("a");
            link.href = url; link.setAttribute("download", name);
            document.body.appendChild(link); link.click();
          } else {
            window.open(url);
          }
        } else setIsError(true);
      })
      .catch(() => setIsError(true))
      .finally(() => setIsCreatingReport(false));
  };

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.source === "uploaded" && row.path) {
      fetchPullingByKey(row.path, row.customName, true);
    } else if (row.source === "app") {
      if (row.defaultUploadPath) {
        fetchPullingByKey(row.defaultUploadPath, row.defaultUploadName ?? row.customName, true);
      } else if (row.appId != null) {
        await downloadPullingPdf(row.appId, row.customName);
      }
    }
  };

  const handleUploadPulling = async ({
    client, well, file, customName, installationDate,
  }: DocumentProps) => {
    try {
      setIsCreatingReport(true);
      setIsModalOpen(false);
      const formData = new FormData();
      formData.append("client", JSON.stringify(client));
      formData.append("well", JSON.stringify(well));
      formData.append("type", "pulling");
      customName && formData.append("customName", customName);
      installationDate && formData.append("installationDate", installationDate.toISOString());
      formData.append("file", file, file.name);
      const res = await (await fetch(process.env.REACT_APP_DEV_API + "/pulling-upload", { method: "POST", body: formData })).json();
      if (res.success) { setIsSuccess(true); selectedClient && handleClientChange(selectedClient); }
      else setIsError(true);
    } catch { setIsError(true); }
    finally { setIsCreatingReport(false); }
  };

  return (
    <>
      <DocumentsPortalLayout
        title="Pulling Reports"
        subtitle="Browse, filter and download pulling reports"
        wells={wells}
        wellsLoading={wellsLoading}
        rows={rows}
        rowsLoading={rowsLoading}
        selectedWellId={selectedWellId}
        onWellSelect={setSelectedWellId}
        onDownload={handleDownload}
        noWellMessage="Select a client to load their pulling reports"
        isAdmin
        clientOptions={clientOptions}
        onClientChange={handleClientChange}
        onUpload={() => setIsModalOpen(true)}
        onOpenInNew={(row) => row.path && fetchPullingByKey(row.path, row.customName, false)}
        onDelete={(row) => { setIdToDelete(row.rowId.replace("u-", "")); setIsOpenAlert(true); }}
        onEdit={(row) => navigate(`/pulling/${row.appId}`)}
        isError={isError}
      />

      {isCreatingReport && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1300 }} />
      )}
      {isSuccess && <AlertComponent type="success" />}
      {isModalOpen && (
        <ModalComponent
          modalContent={
            <UploadFileForm
              clientOptions={clientOptions}
              wellOptions={[]}
              type="pulling"
              fetchDataWells={(id) => handleRequest({ endpoint: `well/client/${id}`, options: { method: "GET" } })}
              onUploadFile={(v: DocumentProps) => handleUploadPulling(v)}
              onCancel={() => setIsModalOpen(false)}
            />
          }
        />
      )}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={<div>Are you sure you want to remove this Pulling Report from the Cloud?</div>}
          onAccept={() => {
            onDeleteDocument("pulling-file", idToDelete, () => selectedClient && handleClientChange(selectedClient));
            setIdToDelete(""); setIsOpenAlert(false);
          }}
          onCancel={() => { setIsOpenAlert(false); setIdToDelete(""); }}
        />
      )}
    </>
  );
};
